import { kontakt, odpiralniCas } from "@/mocks/lokacija";
import { useOpeningStatus } from "@/hooks/useOpeningStatus";

export default function Location() {
  const { todayId, isOpen, statusText, nextOpenText } = useOpeningStatus();

  return (
    <section id="kontakt" className="scroll-mt-24 bg-background-50 py-16 md:py-24 lg:py-28">
      <div className="section-shell">
        <div className="reveal mx-auto max-w-[640px] text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 sm:gap-x-4">
            <span className="font-label text-[10px] font-semibold tracking-[0.2em] text-accent-700">
              05
            </span>
            <span className="hidden h-px w-10 bg-accent-600/60 sm:block" />
            <span className="eyebrow text-secondary-600">Lokacija in odpiralni čas</span>
            <span className="hidden h-px w-10 bg-accent-600/60 sm:block" />
          </div>
          <h2 className="headline mt-5 text-[31px] text-foreground-950 sm:mt-6 sm:text-[39px] lg:text-[48px]">
            Obiščite nas
            <span className="italic text-secondary-600"> na Ptuju.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-[460px] text-[14.5px] leading-relaxed text-foreground-600">
            Zadružni trg 12D, 2250 Ptuj, Slovenija. Ustavite se na kavi ali
            kratkem odmoru — vrata so odprta za vsakdanje trenutke.
          </p>
        </div>

        <div className="reveal mt-9 grid grid-cols-1 gap-5 sm:mt-12 sm:gap-6 lg:grid-cols-[1fr_1.15fr] lg:gap-8">
          <div className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-4 sm:p-7 md:p-9">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-900 text-accent-300">
                <i className="ri-map-pin-2-line text-lg" />
              </span>
              <div>
                <p className="font-label text-[10px] font-semibold uppercase tracking-[0.2em] text-secondary-600">
                  Naslov lokala
                </p>
                <p className="mt-2 font-heading text-xl text-foreground-950">
                  Bar Stanka
                </p>
                <p className="mt-1 text-sm leading-relaxed text-foreground-700">
                  Zadružni trg 12D
                  <br />
                  2250 Ptuj, Slovenija
                </p>
              </div>
            </div>

            <div className="my-7 h-px w-full bg-background-300" />

            <div>
              <div className="flex items-center justify-between gap-3">
                <p className="font-label text-[10px] font-semibold uppercase tracking-[0.2em] text-secondary-600">
                  Odpiralni čas
                </p>
                <span
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 font-label text-[9px] font-semibold uppercase tracking-[0.14em] ${
                    isOpen
                      ? "bg-accent-100 text-secondary-700"
                      : "bg-background-200 text-foreground-600"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isOpen ? "bg-accent-600" : "bg-foreground-400"
                    }`}
                  />
                  {statusText}
                </span>
              </div>

              <ul className="mt-4 divide-y divide-background-300/80">
                {odpiralniCas.map((item) => {
                  const isToday = item.id === todayId;

                  return (
                    <li
                      key={item.id}
                      className={`flex items-center justify-between gap-2.5 py-3 sm:gap-4 ${
                        isToday ? "relative -mx-2.5 rounded-md bg-accent-100/60 px-2.5 sm:-mx-3 sm:px-3" : ""
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-2 text-[12.5px] text-foreground-800 sm:gap-2.5 sm:text-[13.5px]">
                        {item.dnevi}
                        {isToday && (
                          <span className="rounded-full bg-background-50 px-2 py-0.5 font-label text-[8px] font-bold uppercase tracking-[0.14em] text-secondary-600">
                            Danes
                          </span>
                        )}
                      </span>
                      <span
                        className={
                          item.ure === "Zaprto"
                            ? "whitespace-nowrap font-label text-[12px] uppercase tracking-[0.08em] text-secondary-600"
                            : "whitespace-nowrap font-label text-[13px] font-semibold tracking-[0.04em] text-foreground-950"
                        }
                      >
                        {item.ure}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <p className="mt-4 rounded-md bg-accent-100/70 px-4 py-3 text-[12px] leading-relaxed text-foreground-700">
                {!isOpen && nextOpenText ? `${nextOpenText}. ` : ""}
                Odprto smo od ponedeljka do četrtka med 6.00 in 22.00, v petek in
                soboto pa od 6.00 do 24.00. V nedeljo smo odprti od 7.00 do 20.00.
              </p>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href={kontakt.navigacija}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-base flex-1 bg-primary-900 text-background-50 hover:bg-secondary-600"
              >
                <i className="ri-route-line text-base" />
                Prikaži pot
              </a>
              <a
                href={kontakt.googleMaps}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-base flex-1 border-background-400 text-foreground-800 hover:border-foreground-800 hover:bg-foreground-950 hover:text-background-50"
              >
                <i className="ri-map-2-line text-base" />
                Odpri Google Maps
              </a>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
              <a
                href={kontakt.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground-700 transition-colors hover:text-secondary-600"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-900 text-accent-300">
                  <i className="ri-facebook-fill text-sm" />
                </span>
                Facebook
              </a>
              <a
                href={kontakt.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground-700 transition-colors hover:text-secondary-600"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-900 text-accent-300">
                  <i className="ri-instagram-line text-sm" />
                </span>
                Instagram
              </a>
            </div>
          </div>

          <div className="relative min-h-[300px] overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:min-h-[360px] lg:min-h-full">
            <iframe
              title="Zemljevid lokacije Bar Stanka, Zadružni trg 12D, Ptuj"
              src={kontakt.zemljevid}
              className="h-full min-h-[300px] w-full border-0 sm:min-h-[360px] lg:absolute lg:inset-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-md border border-background-200/80 bg-background-50/95 px-3 py-2 backdrop-blur-sm">
              <i className="ri-map-pin-2-fill text-[13px] text-secondary-600" />
              <span className="font-label text-[9px] font-semibold uppercase tracking-[0.14em] text-foreground-800">
                Zadružni trg 12D · Ptuj
              </span>
            </div>
            <a
              href={kontakt.navigacija}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-md border border-background-200/80 bg-background-50/95 px-3 py-2 font-label text-[9px] font-semibold uppercase tracking-[0.14em] text-foreground-800 backdrop-blur-sm transition-colors hover:bg-secondary-500 hover:text-background-50"
            >
              <i className="ri-route-line text-[13px]" />
              Pot do nas
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}